import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Page } from '@trafiku/contracts';
import { toast } from 'sonner';
import { ApiError, api } from './api';
import { t } from './i18n';

export interface ListParams {
  page?: number;
  pageSize?: number;
  sort?: string;
  q?: string;
  [filter: string]: string | number | boolean | undefined;
}

export function useList<T>(resource: string, params: ListParams = {}, enabled = true) {
  return useQuery({
    queryKey: [resource, 'list', params],
    queryFn: () => api<Page<T>>(`/${resource}`, { query: params }),
    placeholderData: keepPreviousData,
    enabled,
  });
}

/** All items of a small collection (e.g. lines for a select). */
export function useAll<T>(resource: string, params: ListParams = {}) {
  return useList<T>(resource, { pageSize: 500, ...params });
}

export function useItem<T>(resource: string, id: string | undefined) {
  return useQuery({
    queryKey: [resource, 'item', id],
    queryFn: () => api<T>(`/${resource}/${id}`),
    enabled: !!id && id !== 'new',
  });
}

/** Create (no id) or replace (with id). Invalidates the resource's queries on success. */
export function useSave<T, I = unknown>(resource: string, id?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: I) =>
      id && id !== 'new'
        ? api<T>(`/${resource}/${id}`, { method: 'PUT', body: input })
        : api<T>(`/${resource}`, { method: 'POST', body: input }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [resource] });
      toast.success(t('Saved'));
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : t('Could not save')),
  });
}

export function useRemove(resource: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api(`/${resource}/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [resource] });
      toast.success(t('Deleted'));
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : t('Could not delete')),
  });
}

export function useAction<T = unknown, I = unknown>(path: string, invalidate: string[], success?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body?: I) => api<T>(path, { method: 'POST', body: body ?? {} }),
    onSuccess: () => {
      for (const key of invalidate) qc.invalidateQueries({ queryKey: [key] });
      toast.success(success ?? t('Done'));
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : t('Failed')),
  });
}

export const fieldErrors = (err: unknown): Record<string, string> =>
  err instanceof ApiError && err.fields ? err.fields : {};
